import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Alert, IconButton, Tooltip, TextField, InputAdornment,
  CircularProgress, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Divider, Grid
} from '@mui/material';
import {
  Search, People, Home, Edit, Save, Close, ListAlt, Article, BeachAccess
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { Active: 'success', Probation: 'info', 'On Leave': 'warning', Terminated: 'error' };

const LeaveEmployeeList = () => {
  const navigate = useNavigate();

  const [employees,   setEmployees]   = useState([]);
  const [leaveGroups, setLeaveGroups] = useState([]);
  const [, setLeaveTypes] = useState([]);
  const [balances,    setBalances]    = useState({});  // { employeeNo: [{ leaveTypeCode, leaveTypeName, balance }] }
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [filterGrp,   setFilterGrp]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Edit dialog
  const [selected,  setSelected]  = useState(null);
  const [form,      setForm]      = useState({});
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: !!selected, value: form, onClose: () => setSelected(null) });
  const [saving,    setSaving]    = useState(false);
  const [dlgError,  setDlgError]  = useState('');
  const [dlgSuccess, setDlgSuccess] = useState('');

  const year = new Date().getFullYear().toString();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, grpRes, typRes] = await Promise.all([
        axios.get(`${API}/api/employees`, { headers: hdrs() }),
        axios.get(`${API}/api/leave/groups`, { headers: hdrs() }),
        axios.get(`${API}/api/leave/types`, { headers: hdrs() }),
      ]);
      setEmployees(empRes.data || []);
      setLeaveGroups(grpRes.data || []);
      setLeaveTypes(typRes.data || []);

      // Load per-employee balances in one shot via the report endpoint
      const rptRes = await axios.get(
        `${API}/api/leave/report?year=${year}`, { headers: hdrs() }
      ).catch(() => ({ data: [] }));

      // Build a map: { employeeNo → [{ leaveTypeCode, leaveTypeName, balance }] }
      const map = {};
      for (const row of (rptRes.data || [])) {
        if (!map[row.employeeNo]) map[row.employeeNo] = [];
        map[row.employeeNo].push({
          leaveTypeCode: row.leaveTypeCode,
          leaveTypeName: row.leaveTypeName,
          balance: row.balance ?? 0,
        });
      }
      setBalances(map);
    } catch { setError('Failed to load employee leave data'); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (emp) => {
    setSelected(emp);
    setForm({ leaveGroupCode: emp.leaveGroupCode || '' });
    setDlgError('');
    setDlgSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setDlgError('');
    try {
      await axios.put(`${API}/api/employees/${selected.employeeNo}`,
        { leaveGroupCode: form.leaveGroupCode },
        { headers: hdrs() }
      );
      setEmployees(prev => prev.map(e =>
        e.employeeNo === selected.employeeNo
          ? { ...e, leaveGroupCode: form.leaveGroupCode }
          : e
      ));
      setSelected(prev => ({ ...prev, leaveGroupCode: form.leaveGroupCode }));
      setDlgSuccess('Saved');
      setTimeout(() => setDlgSuccess(''), 2000);
    } catch (err) {
      setDlgError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      e.employeeNo?.toLowerCase().includes(q) ||
      e.firstName?.toLowerCase().includes(q) ||
      e.lastName?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q);
    const matchGrp    = !filterGrp    || e.leaveGroupCode === filterGrp;
    const matchStatus = !filterStatus || e.status === filterStatus;
    return matchSearch && matchGrp && matchStatus;
  });


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2,
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <BeachAccess sx={{ fontSize: 30 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Leave — Employees</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Leave groups · balances · {year}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Leave Dashboard">
              <IconButton onClick={() => navigate('/hr/leave')}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters */}
        <Card elevation={1} sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" placeholder="Search employee, department…"
                  value={search} onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField select fullWidth size="small" label="Leave Group"
                  value={filterGrp} onChange={e => setFilterGrp(e.target.value)}>
                  <MenuItem value="">All Groups</MenuItem>
                  {leaveGroups.map(g => (
                    <MenuItem key={g.groupCode} value={g.groupCode}>{g.groupCode} — {g.groupName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField select fullWidth size="small" label="Status"
                  value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {['Active', 'Probation', 'On Leave', 'Terminated'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={1} textAlign="right">
                <Typography variant="caption" color="text.secondary">
                  {filtered.length} of {employees.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Table */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Leave Group</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Leave Balances ({year})</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No employees found
                    </TableCell>
                  </TableRow>
                ) : filtered.map(emp => {
                  const empBalances = balances[emp.employeeNo] || [];
                  return (
                    <TableRow key={emp.employeeNo} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {emp.firstName} {emp.lastName}
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                          {emp.employeeNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{emp.department || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={emp.status || 'Active'} size="small"
                          color={STATUS_COLOR[emp.status] || 'default'} />
                      </TableCell>
                      <TableCell>
                        {emp.leaveGroupCode
                          ? <Chip label={emp.leaveGroupCode} size="small" variant="outlined" color="primary" />
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        {empBalances.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">No allocations</Typography>
                        ) : (
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {empBalances.map(b => (
                              <Chip
                                key={b.leaveTypeCode}
                                label={`${b.leaveTypeCode}: ${b.balance}d`}
                                size="small"
                                color={b.balance < 0 ? 'error' : b.balance === 0 ? 'default' : 'success'}
                                variant="outlined"
                                sx={{ fontSize: 10 }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Edit Leave Group">
                            <IconButton size="small" onClick={() => openEdit(emp)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Leave Ledger">
                            <IconButton size="small" color="primary"
                              onClick={() => navigate(`/hr/leave/ledger?employeeNo=${emp.employeeNo}`)}>
                              <ListAlt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Leave Statement">
                            <IconButton size="small" color="secondary"
                              onClick={() => navigate(`/hr/leave/statement?employeeNo=${emp.employeeNo}`)}>
                              <Article fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Card>

      </Box>

      {/* Edit Dialog */}
      {discardDialog}
      <Dialog open={!!selected} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <People fontSize="small" />
            <span>Leave Setup</span>
          </Box>
          <IconButton size="small" onClick={() => setSelected(null)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selected && (
            <>
              <Typography variant="body2" fontWeight={700} mb={0.5}>
                {selected.firstName} {selected.lastName}
              </Typography>
              <Typography variant="caption" fontFamily="monospace" color="text.secondary" display="block" mb={2}>
                {selected.employeeNo} · {selected.department || '—'}
              </Typography>

              <TextField select fullWidth size="small" label="Leave Group"
                value={form.leaveGroupCode || ''}
                onChange={e => setForm(f => ({ ...f, leaveGroupCode: e.target.value }))}
                helperText="Group controls entitlement overrides per leave type">
                <MenuItem value="">(none)</MenuItem>
                {leaveGroups.map(g => (
                  <MenuItem key={g.groupCode} value={g.groupCode}>
                    {g.groupCode} — {g.groupName}
                  </MenuItem>
                ))}
              </TextField>

              {/* Current year balances */}
              {(balances[selected.employeeNo] || []).length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    textTransform="uppercase" letterSpacing={0.8} display="block" mb={0.5}>
                    Balances — {year}
                  </Typography>
                  <Table size="small" sx={{ '& td, & th': { py: 0.4, px: 0.5 } }}>
                    <TableBody>
                      {(balances[selected.employeeNo] || []).map(b => (
                        <TableRow key={b.leaveTypeCode}>
                          <TableCell>
                            <Typography variant="caption">{b.leaveTypeName || b.leaveTypeCode}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={`${b.balance} days`} size="small"
                              color={b.balance < 0 ? 'error' : b.balance === 0 ? 'default' : 'success'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
          {dlgError   && <Alert severity="error"   sx={{ mt: 2 }}>{dlgError}</Alert>}
          {dlgSuccess  && <Alert severity="success" sx={{ mt: 2 }}>Saved successfully</Alert>}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button size="small" variant="outlined"
            onClick={() => navigate(`/hr/leave/ledger?employeeNo=${selected?.employeeNo}`)}>
            <ListAlt fontSize="small" sx={{ mr: 0.5 }} /> Ledger
          </Button>
          <Button size="small" variant="outlined" color="secondary"
            onClick={() => navigate(`/hr/leave/statement?employeeNo=${selected?.employeeNo}`)}>
            <Article fontSize="small" sx={{ mr: 0.5 }} /> Statement
          </Button>
          <Box flex={1} />
          <Button onClick={attemptClose} size="small">Cancel</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : <Save fontSize="small" />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveEmployeeList;
